import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { ActionPillNodeView } from './action-pill-node-view'

export interface ActionPillOptions {
  HTMLAttributes: Record<string, any>
}

export interface ActionPillAttrs {
  provider: string
  action: string
  params: string // JSON-encoded params
  outputVar: string
  label: string
  checkId: string
  tool: string
  /**
   * RFC #323: explicit IntegrationConnection binding. Empty string means
   * "use the run-level connectionId or workspace primary" (legacy
   * behavior). Stored as a plain string attribute so the markdown
   * round-trip is straightforward; the picker UI is Stage 2 work.
   */
  connectionId: string
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    actionPill: {
      insertActionPill: (options: {
        provider?: string
        action?: string
        label?: string
        checkId?: string
        tool?: string
        params?: Record<string, unknown>
        outputVar?: string
        connectionId?: string
      }) => ReturnType
    }
  }
}

export const ActionPillNode = Node.create<ActionPillOptions>({
  name: 'actionPill',

  priority: 1000,

  group: 'inline',

  inline: true,

  atom: true,

  selectable: true,

  draggable: false,

  addOptions() {
    return {
      HTMLAttributes: {},
    }
  },

  addAttributes() {
    return {
      provider: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-provider') || '',
        renderHTML: (attributes) => ({ 'data-provider': attributes.provider }),
      },
      action: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-action') || '',
        renderHTML: (attributes) => ({ 'data-action': attributes.action }),
      },
      params: {
        default: '{}',
        parseHTML: (element) => element.getAttribute('data-params') || '{}',
        renderHTML: (attributes) => ({ 'data-params': attributes.params }),
      },
      outputVar: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-output-var') || '',
        renderHTML: (attributes) => ({ 'data-output-var': attributes.outputVar }),
      },
      label: {
        default: '',
        parseHTML: (element) =>
          element.getAttribute('data-label') || element.textContent || '',
        renderHTML: (attributes) => ({ 'data-label': attributes.label }),
      },
      checkId: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-check-id') || '',
        renderHTML: (attributes) => ({ 'data-check-id': attributes.checkId }),
      },
      tool: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-tool') || '',
        renderHTML: (attributes) => ({ 'data-tool': attributes.tool }),
      },
      connectionId: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-connection-id') || '',
        renderHTML: (attributes) =>
          attributes.connectionId
            ? { 'data-connection-id': attributes.connectionId }
            : {},
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-action-pill]',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const label =
      HTMLAttributes['data-label'] ||
      `${HTMLAttributes['data-provider']}: ${HTMLAttributes['data-action']}`
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        'data-action-pill': '',
        class: 'action-pill',
      }),
      label,
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(ActionPillNodeView)
  },

  addCommands() {
    return {
      insertActionPill:
        (options) =>
        ({ chain }) => {
          // Tool pills derive provider/action from the tool id if unset, so
          // authors can write `<ActionPill tool="policies.list" />` without
          // repeating themselves.
          const [toolNamespace, toolAction] = (options.tool || '').split('.')
          const provider =
            options.provider ?? (options.tool ? 'tool' : '')
          const action =
            options.action ?? toolAction ?? toolNamespace ?? ''
          const label =
            options.label ||
            (options.tool
              ? options.tool
              : `${(provider || '').toUpperCase()}: ${action}`)
          return chain()
            .insertContent({
              type: this.name,
              attrs: {
                provider,
                action,
                label,
                checkId: options.checkId || '',
                tool: options.tool || '',
                params: options.params
                  ? JSON.stringify(options.params)
                  : '{}',
                outputVar: options.outputVar || '',
                connectionId: options.connectionId || '',
              },
            })
            .run()
        },
    }
  },
})
